import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { useEffect, useRef } from "react";
import { Toaster } from "sonner";
import "@/App.css";
import { AuthProvider, useAuth } from "@/lib/auth";
import api from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import AgeGate from "@/components/AgeGate";
import { ShieldCheck } from "lucide-react";
import { Protected } from "@/components/Protected";

import Home from "@/pages/Home";
import About from "@/pages/About";
import Experiences from "@/pages/Experiences";
import Cities from "@/pages/Cities";
import RebelNotes from "@/pages/RebelNotes";
import Privacy from "@/pages/Privacy";
import Contact from "@/pages/Contact";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Dashboard from "@/pages/Dashboard";
import Intake from "@/pages/Intake";
import BookFlow from "@/pages/BookFlow";
import Messages from "@/pages/Messages";
import Journal from "@/pages/Journal";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import BookingConsent from "@/pages/BookingConsent";
import Admin from "@/pages/Admin";

function AuthCallback() {
  const location = useLocation();
  const nav = useNavigate();
  const done = useRef(false);
  const { refresh } = useAuth();
  useEffect(() => {
    if (done.current) return; done.current = true;
    const hash = location.hash || window.location.hash;
    const sid = new URLSearchParams(hash.replace("#", "")).get("session_id");
    (async () => {
      try {
        await api.post("/auth/google/session", { session_id: sid });
        window.history.replaceState({}, "", "/dashboard");
        await refresh();
        nav("/dashboard", { replace: true });
      } catch (e) {
        nav("/login?error=google", { replace: true });
      }
    })();
  }, []); // eslint-disable-line
  return <div className="min-h-screen flex items-center justify-center text-zinc-400 text-xs font-mono-eyebrow uppercase tracking-[0.28em]">Entering the private portal…</div>;
}

function Shell() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <>
      <AgeGate />
      <Header />
      <main className="pt-20">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/experiences" element={<Experiences />} />
          <Route path="/sessions" element={<Navigate to="/experiences" replace />} />
          <Route path="/pricing" element={<Navigate to="/experiences" replace />} />
          <Route path="/cities" element={<Cities />} />
          <Route path="/rebel-notes" element={<RebelNotes />} />
          <Route path="/resources" element={<Navigate to="/rebel-notes" replace />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
          <Route path="/dashboard/intake" element={<Protected><Intake /></Protected>} />
          <Route path="/dashboard/book" element={<Protected><BookFlow /></Protected>} />
          <Route path="/dashboard/messages" element={<Protected><Messages /></Protected>} />
          <Route path="/dashboard/journal" element={<Protected><Journal /></Protected>} />
          <Route path="/dashboard/consent/:booking_id" element={<Protected><BookingConsent /></Protected>} />
          <Route path="/admin" element={<Protected adminOnly><Admin /></Protected>} />
        </Routes>
      </main>
      <Footer />
      <a
        href="https://www.google.com"
        data-testid="quick-exit-btn"
        title="Quick exit"
        aria-label="Quick exit"
        rel="noopener"
        className="fixed bottom-5 right-5 z-[55] rc-btn rc-btn-blue shadow-lg"
        style={{ fontSize: "0.75rem", padding: "0.6rem 1rem", minHeight: 40 }}
      >
        <ShieldCheck className="w-4 h-4" /> Exit
      </a>
      <Toaster theme="dark" position="bottom-right" toastOptions={{ style: { background: "#111114", border: "1px solid rgba(255,255,255,0.08)", color: "#e8e8ec" } }} />
    </>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Shell />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
